import React, { useState } from 'react';
import { UserIcon, MailIcon, PhoneIcon, BikeIcon, ShieldCheckIcon, UsersIcon, SendIcon, CheckCircleIcon, AlertCircleIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
interface ContactFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  bikeModel: string;
  engineSize: string;
  raceClass: string;
  licence: string;
  teamName: string;
}
interface ContactFormErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  bikeModel?: string;
  engineSize?: string;
  raceClass?: string;
  licence?: string;
}
export function ContactForm() {
  const {
    t
  } = useLanguage();
  const [formData, setFormData] = useState<ContactFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    bikeModel: '',
    engineSize: '',
    raceClass: '',
    licence: '',
    teamName: ''
  });
  const [errors, setErrors] = useState<ContactFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  function validateContactForm(data: ContactFormData): ContactFormErrors {
    const newErrors: ContactFormErrors = {};
    if (!data.firstName.trim()) newErrors.firstName = t('contact.errorFirstName');
    if (!data.lastName.trim()) newErrors.lastName = t('contact.errorLastName');
    if (!data.email.trim()) {
      newErrors.email = t('contact.errorEmail');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data.email)) {
      newErrors.email = t('contact.errorEmailInvalid');
    }
    if (!data.phone.trim()) {
      newErrors.phone = t('contact.errorPhone');
    } else if (!/^[+]?[\d\s\-()]{7,}$/.test(data.phone)) {
      newErrors.phone = t('contact.errorPhoneInvalid');
    }
    if (!data.bikeModel.trim()) newErrors.bikeModel = t('contact.errorBikeModel');
    if (!data.engineSize.trim()) {
      newErrors.engineSize = t('contact.errorEngineSize');
    } else if (isNaN(Number(data.engineSize)) || Number(data.engineSize) <= 0) {
      newErrors.engineSize = t('contact.errorEngineSizeInvalid');
    }
    if (!data.raceClass) newErrors.raceClass = t('contact.errorRaceClass');
    if (data.raceClass === 'pro' && !data.licence.trim()) {
      newErrors.licence = t('contact.errorLicense');
    }
    return newErrors;
  }
  function handleChange(e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name as keyof ContactFormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  }
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const validationErrors = validateContactForm(formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setIsSubmitting(true);
    setErrors({});
    try {
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          access_key: '58247f00-ccb7-40b2-8571-dd9f583b0c38',
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          bikeModel: formData.bikeModel,
          engineSize: formData.engineSize,
          raceClass: formData.raceClass,
          licence: formData.licence,
          teamName: formData.teamName
        })
      });
      const result = await response.json();
      if (result.success) {
        setSubmitStatus('success');
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          bikeModel: '',
          engineSize: '',
          raceClass: '',
          licence: '',
          teamName: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  }
  if (submitStatus === 'success') {
    return <section id="contact" className="w-full bg-neutral-950 bg-noise py-20 md:py-28 px-4" data-id="element-28">
        <div className="max-w-2xl mx-auto text-center" data-id="element-29">
          <div className="w-20 h-20 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-6" data-id="element-30">
            <CheckCircleIcon className="h-10 w-10 text-green-500" data-id="element-31" />
          </div>
          <h2 className="font-display text-4xl md:text-5xl tracking-wider text-white mb-4" data-id="element-32">
            {t('contact.successTitle')}
          </h2>
          <p className="text-gray-400 text-lg mb-8" data-id="element-33">
            {t('contact.successMessage')}
          </p>
          <button onClick={() => setSubmitStatus('idle')} className="px-8 py-3 bg-orange-500 hover:bg-orange-600 text-black font-bold uppercase tracking-wider rounded transition-colors" data-id="element-34">
            {t('contact.registerAnother')}
          </button>
        </div>
      </section>;
  }
  return <section id="contact" className="w-full bg-neutral-950 bg-noise py-20 md:py-28 px-4" data-id="element-35">
      <div className="max-w-3xl mx-auto" data-id="element-36">
        {/* Section Header */}
        <div className="text-center mb-12" data-id="element-37">
          <p className="text-orange-500 font-semibold text-sm uppercase tracking-[0.3em] mb-3" data-id="element-38">
            {t('contact.secureSpot')}
          </p>
          <h2 className="font-display text-4xl md:text-6xl tracking-wider text-white mb-4" data-id="element-39">
            {t('contact.title')}
          </h2>
          <p className="text-gray-400 text-lg max-w-xl mx-auto" data-id="element-40">
            {t('contact.subtitle')}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} noValidate className="bg-neutral-900 border border-neutral-800 rounded-2xl p-6 md:p-10" data-id="element-41">
          {submitStatus === 'error' && <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl flex items-center gap-3" data-id="element-42">
              <AlertCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0" data-id="element-43" />
              <p className="text-red-400 text-sm" data-id="element-44">
                {t('contact.errorMessage')}
              </p>
            </div>}

          {/* Name Fields */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5" data-id="element-45">
            <div data-id="element-46">
              <label htmlFor="contact-firstName" className="block text-sm font-semibold text-gray-300 mb-2" data-id="element-47">
                <span className="flex items-center gap-2" data-id="element-48">
                  <UserIcon className="h-4 w-4 text-gray-500" data-id="element-49" />
                  {t('contact.firstName')} {t('contact.required')}
                </span>
              </label>
              <input type="text" id="contact-firstName" name="firstName" value={formData.firstName} onChange={handleChange} placeholder={t('contact.enterFirstName')} className={`w-full px-4 py-3 bg-neutral-800 border rounded-lg text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors ${errors.firstName ? 'border-red-500' : 'border-neutral-700 hover:border-neutral-600'}`} aria-invalid={!!errors.firstName} aria-describedby={errors.firstName ? 'contact-firstName-error' : undefined} data-id="element-50" />
              {errors.firstName && <p id="contact-firstName-error" className="mt-1.5 text-xs text-red-400" data-id="element-51">
                  {errors.firstName}
                </p>}
            </div>
            <div data-id="element-52">
              <label htmlFor="contact-lastName" className="block text-sm font-semibold text-gray-300 mb-2" data-id="element-53">
                <span className="flex items-center gap-2" data-id="element-54">
                  <UserIcon className="h-4 w-4 text-gray-500" data-id="element-55" />
                  {t('contact.lastName')} {t('contact.required')}
                </span>
              </label>
              <input type="text" id="contact-lastName" name="lastName" value={formData.lastName} onChange={handleChange} placeholder={t('contact.enterLastName')} className={`w-full px-4 py-3 bg-neutral-800 border rounded-lg text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors ${errors.lastName ? 'border-red-500' : 'border-neutral-700 hover:border-neutral-600'}`} aria-invalid={!!errors.lastName} aria-describedby={errors.lastName ? 'contact-lastName-error' : undefined} data-id="element-56" />
              {errors.lastName && <p id="contact-lastName-error" className="mt-1.5 text-xs text-red-400" data-id="element-57">
                  {errors.lastName}
                </p>}
            </div>
          </div>

          {/* Email */}
          <div className="mb-5" data-id="element-58">
            <label htmlFor="contact-email" className="block text-sm font-semibold text-gray-300 mb-2" data-id="element-59">
              <span className="flex items-center gap-2" data-id="element-60">
                <MailIcon className="h-4 w-4 text-gray-500" data-id="element-61" />
                {t('contact.email')} {t('contact.required')}
              </span>
            </label>
            <input type="email" id="contact-email" name="email" value={formData.email} onChange={handleChange} placeholder="your@email.com" className={`w-full px-4 py-3 bg-neutral-800 border rounded-lg text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors ${errors.email ? 'border-red-500' : 'border-neutral-700 hover:border-neutral-600'}`} aria-invalid={!!errors.email} aria-describedby={errors.email ? 'contact-email-error' : undefined} data-id="element-62" />
            {errors.email && <p id="contact-email-error" className="mt-1.5 text-xs text-red-400" data-id="element-63">
                {errors.email}
              </p>}
          </div>

          {/* Phone */}
          <div className="mb-5" data-id="element-64">
            <label htmlFor="contact-phone" className="block text-sm font-semibold text-gray-300 mb-2" data-id="element-65">
              <span className="flex items-center gap-2" data-id="element-66">
                <PhoneIcon className="h-4 w-4 text-gray-500" data-id="element-67" />
                {t('contact.phone')} {t('contact.required')}
              </span>
            </label>
            <input type="tel" id="contact-phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="+371 20000000" className={`w-full px-4 py-3 bg-neutral-800 border rounded-lg text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors ${errors.phone ? 'border-red-500' : 'border-neutral-700 hover:border-neutral-600'}`} aria-invalid={!!errors.phone} aria-describedby={errors.phone ? 'contact-phone-error' : undefined} data-id="element-68" />
            {errors.phone && <p id="contact-phone-error" className="mt-1.5 text-xs text-red-400" data-id="element-69">
                {errors.phone}
              </p>}
          </div>

          {/* Bike Details */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-5" data-id="element-70">
            <div data-id="element-71">
              <label htmlFor="contact-bikeModel" className="block text-sm font-semibold text-gray-300 mb-2" data-id="element-72">
                <span className="flex items-center gap-2" data-id="element-73">
                  <BikeIcon className="h-4 w-4 text-gray-500" data-id="element-74" />
                  {t('contact.bikeModel')} {t('contact.required')}
                </span>
              </label>
              <input type="text" id="contact-bikeModel" name="bikeModel" value={formData.bikeModel} onChange={handleChange} placeholder="e.g. KTM 300 EXC" className={`w-full px-4 py-3 bg-neutral-800 border rounded-lg text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors ${errors.bikeModel ? 'border-red-500' : 'border-neutral-700 hover:border-neutral-600'}`} aria-invalid={!!errors.bikeModel} aria-describedby={errors.bikeModel ? 'contact-bikeModel-error' : undefined} data-id="element-75" />
              {errors.bikeModel && <p id="contact-bikeModel-error" className="mt-1.5 text-xs text-red-400" data-id="element-76">
                  {errors.bikeModel}
                </p>}
            </div>
            <div data-id="element-77">
              <label htmlFor="contact-engineSize" className="block text-sm font-semibold text-gray-300 mb-2" data-id="element-78">
                {t('contact.engineSize')} {t('contact.required')}
              </label>
              <input type="text" id="contact-engineSize" name="engineSize" value={formData.engineSize} onChange={handleChange} placeholder="e.g. 300" className={`w-full px-4 py-3 bg-neutral-800 border rounded-lg text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors ${errors.engineSize ? 'border-red-500' : 'border-neutral-700 hover:border-neutral-600'}`} aria-invalid={!!errors.engineSize} aria-describedby={errors.engineSize ? 'contact-engineSize-error' : undefined} data-id="element-79" />
              {errors.engineSize && <p id="contact-engineSize-error" className="mt-1.5 text-xs text-red-400" data-id="element-80">
                  {errors.engineSize}
                </p>}
            </div>
          </div>

          {/* Race Class */}
          <div className="mb-5" data-id="element-81">
            <label htmlFor="contact-raceClass" className="block text-sm font-semibold text-gray-300 mb-2" data-id="element-82">
              {t('contact.raceClass')} {t('contact.required')}
            </label>
            <select id="contact-raceClass" name="raceClass" value={formData.raceClass} onChange={handleChange} className={`w-full px-4 py-3 bg-neutral-800 border rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors appearance-none ${errors.raceClass ? 'border-red-500' : 'border-neutral-700 hover:border-neutral-600'} ${!formData.raceClass ? 'text-gray-600' : ''}`} aria-invalid={!!errors.raceClass} aria-describedby={errors.raceClass ? 'contact-raceClass-error' : undefined} data-id="element-83">
              <option value="" disabled data-id="element-84">
                {t('contact.selectClass')}
              </option>
              <option value="beginner" data-id="element-85">
                {t('contact.beginnerOption')} - €100 (10 km)
              </option>
              <option value="hobby" data-id="element-86">
                {t('contact.hobbyOption')} — €100 (12 km)
              </option>
              <option value="pro" data-id="element-87">
                {t('contact.proOption')} — €100 (13 km)
              </option>
            </select>
            {errors.raceClass && <p id="contact-raceClass-error" className="mt-1.5 text-xs text-red-400" data-id="element-88">
                {errors.raceClass}
              </p>}
          </div>

          {/* Racing Licence */}
          <div className="mb-5" data-id="element-89">
            <label htmlFor="contact-licence" className="block text-sm font-semibold text-gray-300 mb-2" data-id="element-90">
              <span className="flex items-center gap-2" data-id="element-91">
                <ShieldCheckIcon className="h-4 w-4 text-gray-500" data-id="element-92" />
                {t('contact.licenseNumber')}{' '}
                {formData.raceClass === 'pro' ? t('contact.required') : t('contact.optional')}
              </span>
            </label>
            <input type="text" id="contact-licence" name="licence" value={formData.licence} onChange={handleChange} placeholder="FMN/FIM licences numurs" className={`w-full px-4 py-3 bg-neutral-800 border rounded-lg text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors ${errors.licence ? 'border-red-500' : 'border-neutral-700 hover:border-neutral-600'}`} aria-invalid={!!errors.licence} aria-describedby={errors.licence ? 'contact-licence-error' : undefined} data-id="element-93" />
            {errors.licence && <p id="contact-licence-error" className="mt-1.5 text-xs text-red-400" data-id="element-94">
                {errors.licence}
              </p>}
            {formData.raceClass === 'pro' && <p className="mt-1.5 text-xs text-orange-400" data-id="element-95"></p>}
          </div>

          {/* Team Name (Optional) */}
          <div className="mb-8" data-id="element-96">
            <label htmlFor="contact-teamName" className="block text-sm font-semibold text-gray-300 mb-2" data-id="element-97">
              <span className="flex items-center gap-2" data-id="element-98">
                <UsersIcon className="h-4 w-4 text-gray-500" data-id="element-99" />
                {t('contact.teamName')}
              </span>
            </label>
            <input type="text" id="contact-teamName" name="teamName" value={formData.teamName} onChange={handleChange} placeholder={t('contact.enterTeamName')} className="w-full px-4 py-3 bg-neutral-800 border border-neutral-700 hover:border-neutral-600 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors" data-id="element-100" />
          </div>

          {/* Submit */}
          <button type="submit" disabled={isSubmitting} className="w-full py-4 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/50 disabled:cursor-not-allowed text-black font-bold text-lg uppercase tracking-wider rounded-lg transition-all flex items-center justify-center gap-3" data-id="element-101">
            {isSubmitting ? <>
                <svg className="animate-spin h-5 w-5 text-black" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true" data-id="element-102">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-103" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-104" />
                </svg>
                {t('contact.submitting')}
              </> : <>
                <SendIcon className="h-5 w-5" data-id="element-105" />
                {t('contact.submit')}
              </>}
          </button>

          <p className="mt-4 text-center text-xs text-gray-500" data-id="element-106">
            {t('contact.disclaimer')}
          </p>
        </form>
      </div>
    </section>;
}